import React from "react";
import {
  Typography,
  Button,
  Box,
  TextField,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import Edvisor_Logo_main from "../images/Edvisor_Logo_main.png";
import { EdvisorLogin, GetMenuDetails } from "./EdvisorLoginAction";

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

  @keyframes shimmerBtn {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes lineExpand {
    from { transform: scaleX(0); opacity: 0; }
    to   { transform: scaleX(1); opacity: 1; }
  }
  @keyframes particle {
    0%   { transform: translateY(0)      translateX(0);      opacity: 0;   }
    10%  { opacity: 0.9; }
    90%  { opacity: 0.4; }
    100% { transform: translateY(-110vh) translateX(var(--dx)); opacity: 0; }
  }
  @keyframes orbPulse1 {
    0%,100% { transform: scale(1)    translate(0,0);       opacity:.55; }
    50%      { transform: scale(1.12) translate(30px,-20px); opacity:.75; }
  }
  @keyframes orbPulse2 {
    0%,100% { transform: scale(1)    translate(0,0);        opacity:.4; }
    50%      { transform: scale(1.08) translate(-25px,18px); opacity:.6; }
  }
  @keyframes orbPulse3 {
    0%,100% { transform: scale(1)    translate(0,0);      opacity:.3; }
    50%      { transform: scale(1.15) translate(10px,25px); opacity:.5; }
  }
  @keyframes iconGlow {
    0%,100% { filter: drop-shadow(0 0  8px rgba(0,210,200,.4)); }
    50%      { filter: drop-shadow(0 0 18px rgba(0,210,200,.85)); }
  }
  @keyframes cardIn {
    from { opacity:0; transform: translateY(30px) scale(.97); }
    to   { opacity:1; transform: translateY(0)    scale(1);   }
  }
  .ea-particle {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0,220,210,.85) 0%, rgba(0,160,190,.3) 60%, transparent 100%);
    animation: particle var(--dur) var(--delay) ease-in infinite;
    pointer-events: none;
  }
`;

const C = {
  teal: "#00d2c8",
  tealLight: "#40e8e0",
  tealDark: "#00a89e",
  cyan: "#00b4d8",
  cyanLight: "#48cae4",
  warmGray: "#7ab0b8",
  border: "rgba(0,210,200,.18)",
  white: "#e8f4f6",
  cardBg: "rgba(12,28,44,.95)",
};

// 28 floating particles
const PARTICLES = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  size: Math.random() * 4 + 2,
  left: Math.random() * 100,
  dur: Math.random() * 12 + 8,
  delay: Math.random() * 10,
  dx: (Math.random() - 0.5) * 120,
}));

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(0,210,200,.04)",
    borderRadius: "10px",
    fontFamily: "'DM Sans', sans-serif",
    color: C.white,
    fontSize: "0.95rem",
    transition: "all .25s ease",
    "& fieldset": {
      borderColor: C.border,
      borderWidth: "1px",
      transition: "border-color .25s ease",
    },
    "&:hover fieldset": { borderColor: "rgba(0,210,200,.42)" },
    "&.Mui-focused fieldset": {
      borderColor: C.teal,
      borderWidth: "1.5px",
      boxShadow: "0 0 0 3px rgba(0,210,200,.1)",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'DM Sans', sans-serif",
    color: C.warmGray,
    fontSize: "0.9rem",
    "&.Mui-focused": { color: C.teal },
  },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": {
    fontSize: "1.2rem",
    color: C.warmGray,
  },
  "& input": {
    color: C.white,
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 100px #0b1e30 inset",
      WebkitTextFillColor: C.white,
    },
  },
};

const OTPreAuthLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPwd, setShowPwd] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [logoError, setLogoError] = React.useState(false);

  // Add a ref to track if login was manually triggered
  const isManualSubmitRef = React.useRef(false);

  // Get states from Redux
  const edvisorLogin = useSelector((state: any) => state.edvisorLogin);
  const loginResponse = edvisorLogin?.loginResponse;
  const menuItems = edvisorLogin?.menuItems;
  const menuResponse = edvisorLogin?.menuResponse;
  const loading = edvisorLogin?.loading;

  // Debug logs
  React.useEffect(() => {
  }, [edvisorLogin, loginResponse, menuResponse, menuItems, loading]);

  // Handle login response
  React.useEffect(() => {

    if (loginResponse) {
      if (loginResponse.loginSuccess === true) {

        // Save token
        if (loginResponse.generatedLink) {
          localStorage.setItem("token", loginResponse.generatedLink);
        } else {
          localStorage.setItem("token", "dummy_token");
        }

        // Save COMPLETE user data
        localStorage.setItem("userData", JSON.stringify(loginResponse));

        // Store individual fields for easy access
        localStorage.setItem("userId", loginResponse.employeeId?.toString() || "");
        localStorage.setItem("userName", loginResponse.employeeName || "");
        localStorage.setItem("userEmail", loginResponse.email || "");
        localStorage.setItem("userPassword", password);
        localStorage.setItem("RoleID", loginResponse.roleId);
        localStorage.setItem("RoleName", loginResponse.roleName);
        sessionStorage.setItem("RoleCode", loginResponse.roleCode);

        // Store in sessionStorageRoleCode
        if (loginResponse.employeeCode) {
          sessionStorage.setItem("employeeCode", loginResponse.employeeCode);
        }
        if (loginResponse.employeeName) {
          sessionStorage.setItem("employeeName", loginResponse.employeeName);
        }
        if (loginResponse.employeeId) {
          sessionStorage.setItem("employeeId", loginResponse.employeeId.toString());
        }

        // Clear any old menu items
        localStorage.removeItem("menuItems");

        // Now fetch menu details
        const menuData = {
          userID: email,
          userPassword: password
        };

        dispatch(GetMenuDetails(menuData) as any);

      } else {
        setErrorMessage(loginResponse.message || "Invalid credentials. Please try again.");
        setOpenSnackbar(true);
        setIsLoading(false);
        isManualSubmitRef.current = false;
      }
    }
  }, [loginResponse, dispatch, email, password]);

  // Handle menu response and navigation
  React.useEffect(() => {


    if (menuItems && menuItems.length > 0) {

      const validMenuItems = menuItems.filter((item: any) =>
        item && item.MenuName && item.MenuName !== "null" && item.MenuName !== ""
      );

      if (validMenuItems.length > 0) {
        localStorage.setItem("menuItems", JSON.stringify(validMenuItems));

        // Store default route
        const noticeMenu = validMenuItems.find((item: any) =>
          item.MenuName && item.MenuName.toLowerCase().includes('notice')
        );

        if (noticeMenu) {
          localStorage.setItem("defaultRoute", noticeMenu.MenuPath);
        }

        // Navigate to authorize
        setTimeout(() => {
          // Use window.location for a full page reload to ensure clean state
          window.location.href = "/authorize";
        }, 100);
      }
    }
  }, [menuItems, menuResponse]);

  // Handle loading state
  React.useEffect(() => {
    if (loading !== undefined) {
      setIsLoading(loading);
    }
  }, [loading]);

  // Check for stored data on mount
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("userData");
    const menuItems = localStorage.getItem("menuItems");

    if (token && userData && menuItems) {
      window.location.href = "/authorize";
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    isManualSubmitRef.current = true;

    if (!email || !password) {
      setErrorMessage("Please enter both email and password");
      setOpenSnackbar(true);
      isManualSubmitRef.current = false;
      return;
    }


    const loginData = {
      userID: email,
      userPassword: password
    };

    setIsLoading(true);
    setErrorMessage("");
    setOpenSnackbar(false);

    try {
      // Clear any existing data before new login
      localStorage.clear();
      sessionStorage.clear();

      await dispatch(EdvisorLogin(loginData) as any);
    } catch (error: any) {
      console.error("Login error:", error);
      setErrorMessage(error?.message || "An error occurred during login");
      setOpenSnackbar(true);
      setIsLoading(false);
      isManualSubmitRef.current = false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleInvalid = (e: React.InvalidEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/forgot-password");
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setErrorMessage("");
  };

  const handleLogoError = () => {
    console.error("Logo failed to load");
    setLogoError(true);
  };

  const renderParticles = () => {
    return PARTICLES.map((particle) => (
      <Box
        key={particle.id}
        className="ea-particle"
        sx={{
          width: particle.size,
          height: particle.size,
          left: `${particle.left}%`,
          bottom: "-10%",
          "--dur": `${particle.dur}s`,
          "--delay": `${particle.delay}s`,
          "--dx": `${particle.dx}px`,
        }}
      />
    ));
  };

  return (
    <>
      <style>{globalStyles}</style>

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(145deg,#04111a 0%,#091e2e 35%,#0d2840 60%,#06141e 100%)",
          position: "relative",
          overflow: "hidden",
          p: 2,
        }}
      >
        {/* Floating orbs - same as before */}
        <Box
          sx={{
            position: "absolute",
            width: "50vmax",
            height: "50vmax",
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 30% 30%, rgba(0,210,200,.2) 0%, transparent 70%)",
            top: "-20vmax",
            right: "-10vmax",
            filter: "blur(60px)",
            animation: "orbPulse1 14s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: "45vmax",
            height: "45vmax",
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 70% 70%, rgba(0,180,216,.15) 0%, transparent 70%)",
            bottom: "-15vmax",
            left: "-10vmax",
            filter: "blur(60px)",
            animation: "orbPulse2 18s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: "40vmax",
            height: "40vmax",
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 40% 60%, rgba(72,202,228,.1) 0%, transparent 70%)",
            top: "10%",
            left: "20%",
            filter: "blur(70px)",
            animation: "orbPulse3 16s ease-in-out infinite",
          }}
        />

        {/* Subtle grid overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(0,210,200,.02) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,210,200,.02) 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />

        {/* Particles */}
        {renderParticles()}

        {/* Card */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 520,
            background:
              "linear-gradient(160deg, rgba(12,28,45,.96) 0%, rgba(7,18,32,.98) 100%)",
            borderRadius: "20px",
            border: `1px solid ${C.border}`,
            boxShadow: `
            0 0 0 1px rgba(0,210,200,.06),
            0 24px 60px rgba(0,0,0,.75),
            0 0 80px rgba(0,150,160,.08),
            inset 0 1px 0 rgba(0,210,200,.08)`,
            overflow: "hidden",
            animation: "cardIn .7s cubic-bezier(.16,1,.3,1) both",
            backdropFilter: "blur(20px)",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Shimmer top line */}
          <Box
            sx={{
              height: "2px",
              background: `linear-gradient(90deg, transparent, ${C.teal}, ${C.cyanLight}, ${C.teal}, transparent)`,
              backgroundSize: "200% auto",
              animation: "shimmerBtn 3.5s linear infinite",
            }}
          />

          <Box sx={{ p: { xs: 4, sm: 5 } }}>
            {/* Error Alert */}
            {errorMessage && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: "8px",
                  fontFamily: "'DM Sans', sans-serif",
                  animation: "fadeSlideUp .3s ease both",
                }}
                onClose={() => setErrorMessage("")}
              >
                {errorMessage}
              </Alert>
            )}

            {/* Brand */}
            <Box
              sx={{
                textAlign: "center",
                mb: 4,
                animation: "fadeSlideUp .5s ease .1s both",
                opacity: 0,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "20px",
                  mx: "auto",
                  mb: 3,
                  background:
                    "linear-gradient(135deg, rgba(0,210,200,.12) 0%, rgba(0,150,180,.06) 100%)",
                  border: "1px solid rgba(0,210,200,.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 24px rgba(0,210,200,.15)",
                  animation: "iconGlow 3s ease-in-out infinite",
                }}
              >
                <img
                  src={logoError ? "/Edvisor_Logo_main.png" : Edvisor_Logo_main}
                  alt="Company Logo"
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "14px",
                    filter: "drop-shadow(0 0 6px rgba(0,210,200,.5))",
                    objectFit: "contain",
                  }}
                  onError={handleLogoError}
                />
              </Box>

              <Typography
                sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: C.white,
                  mb: 1,
                  letterSpacing: "-0.02em",
                }}
              >
                Edvisor{" "}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(90deg, ${C.teal}, ${C.cyanLight})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Pvt Ltd
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "1rem",
                  fontWeight: 400,
                  color: C.warmGray,
                  letterSpacing: "0.02em",
                }}
              >
                Sign in to your account to continue
              </Typography>
            </Box>

            {/* Divider */}
            <Box
              sx={{
                height: "1px",
                mb: 3.5,
                transformOrigin: "center",
                background:
                  "linear-gradient(90deg, transparent, rgba(0,210,200,.2), transparent)",
                animation: "lineExpand .8s ease .4s both",
              }}
            />

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Box
                sx={{
                  mb: 3,
                  animation: "fadeSlideUp .5s ease .2s both",
                  opacity: 0,
                }}
              >
                <TextField
                  fullWidth
                  label="Email Address *"
                  name="email"
                  type="email"
                  autoComplete="off"
                  autoFocus
                  variant="outlined"
                  size="medium"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onInvalid={handleInvalid}
                  required
                  disabled={isLoading}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box
                sx={{
                  mb: 2.5,
                  animation: "fadeSlideUp .5s ease .3s both",
                  opacity: 0,
                }}
              >
                <TextField
                  fullWidth
                  label="Password *"
                  name="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="off"
                  variant="outlined"
                  size="medium"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onInvalid={handleInvalid}
                  required
                  disabled={isLoading}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPwd(!showPwd)}
                          edge="end"
                          size="small"
                          disabled={isLoading}
                          sx={{
                            color: C.warmGray,
                            "&:hover": { color: C.teal },
                          }}
                        >
                          {showPwd ? (
                            <VisibilityOffOutlinedIcon
                              sx={{ fontSize: "1.2rem" }}
                            />
                          ) : (
                            <VisibilityOutlinedIcon
                              sx={{ fontSize: "1.2rem" }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box
              >
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.8,
                    borderRadius: "10px",
                    background: isLoading
                      ? "rgba(0,210,200,.5)"
                      : `linear-gradient(90deg, ${C.tealDark} 0%, ${C.teal} 40%, ${C.cyanLight} 60%, ${C.cyan} 100%)`,
                    backgroundSize: "200% auto",
                    color: "#041018",
                    fontSize: "1rem",
                    fontWeight: 700,
                    fontFamily: "'DM Sans', sans-serif",
                    letterSpacing: "0.06em",
                    textTransform: "none",
                    border: "none",
                    boxShadow: isLoading
                      ? "none"
                      : "0 6px 24px rgba(0,210,200,.35), 0 2px 8px rgba(0,0,0,.4)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(135deg, transparent 30%, rgba(255,255,255,.22) 50%, transparent 70%)",
                      transform: "translateX(-100%)",
                    },
                    "&:hover:not(:disabled)::before": { transform: "translateX(100%)" },
                    "&:hover:not(:disabled)": {
                      backgroundPosition: "right center",
                      boxShadow:
                        "0 10px 32px rgba(0,210,200,.52), 0 0 22px rgba(0,210,200,.2)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: "#041018" }} />
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </Box>

              {/* Forgot Password */}
              <Box
                sx={{
                  textAlign: "center",
                  mt: 2,
                  mb: 2.5,
                  animation: "fadeSlideUp .5s ease .4s both",
                  opacity: 0,
                }}
              >
                <Link
                  href="#"
                  onClick={handleForgotPassword}
                  underline="none"
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "0.9rem",
                    color: C.warmGray,
                    transition: "color .2s",
                    "&:hover": { color: C.teal },
                    cursor: "pointer",
                  }}
                >
                  Forgot password?
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Snackbar for error messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{
            width: "100%",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OTPreAuthLoginPage;